import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { contractRequestAPI } from '@/lib/api';
import { formatDate } from '@/lib/utils';
import { CalendarClock, CheckCircle2, AlertTriangle, Edit, Send } from 'lucide-react';

const WAITING_PERIOD_OPTIONS = [
  { value: '60_days', label: '60 Days' },
  { value: '90_days', label: '90 Days' },
  { value: '6_months', label: '6 Months' },
  { value: 'blockout', label: 'Blockout Date' },
  { value: 'custom', label: 'Custom' },
];

function addDaysISO(iso, days) {
  if (!iso) return '';
  try {
    const d = new Date(iso + 'T00:00:00Z');
    d.setUTCDate(d.getUTCDate() + days);
    return d.toISOString().slice(0, 10);
  } catch {
    return '';
  }
}

function computeFollowUp({ release_sent_date, waiting_period_type, blockout_date }) {
  if (waiting_period_type === 'blockout') return blockout_date || '';
  if (!release_sent_date || !waiting_period_type) return '';
  if (waiting_period_type === '60_days') return addDaysISO(release_sent_date, 60);
  if (waiting_period_type === '90_days') return addDaysISO(release_sent_date, 90);
  if (waiting_period_type === '6_months') return addDaysISO(release_sent_date, 183);
  return '';
}

export function ReleaseFollowUpCard({ request, carriers = [], isKR, onChange }) {
  const rfu = request?.release_followup;
  const [editOpen, setEditOpen] = useState(false);
  const [completeOpen, setCompleteOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [completionNotes, setCompletionNotes] = useState('');
  const [form, setForm] = useState({
    release_required: true,
    release_sent: false,
    release_sent_date: '',
    waiting_period_type: '',
    follow_up_date: '',
    blockout_date: '',
    carrier_id: request?.carrier_id || '',
    notes: '',
  });
  const [followUpManuallyEdited, setFollowUpManuallyEdited] = useState(false);

  // When dialog opens, prime form with current rfu
  const openEdit = () => {
    if (rfu) {
      setForm({
        release_required: rfu.release_required ?? true,
        release_sent: rfu.release_sent ?? false,
        release_sent_date: rfu.release_sent_date || '',
        waiting_period_type: rfu.waiting_period_type || '',
        follow_up_date: rfu.follow_up_date || '',
        blockout_date: rfu.blockout_date || '',
        carrier_id: rfu.carrier_id || request?.carrier_id || '',
        notes: rfu.notes || '',
      });
    } else {
      setForm({
        release_required: true,
        release_sent: false,
        release_sent_date: '',
        waiting_period_type: '',
        follow_up_date: '',
        blockout_date: '',
        carrier_id: request?.carrier_id || '',
        notes: '',
      });
    }
    setFollowUpManuallyEdited(false);
    setEditOpen(true);
  };

  // Auto-recalculate follow-up date when inputs change (unless user manually edited it)
  const autoFollowUp = useMemo(() => computeFollowUp(form), [form]);
  const effectiveFollowUp = followUpManuallyEdited ? form.follow_up_date : (autoFollowUp || form.follow_up_date);

  const handleSave = async (e) => {
    e?.preventDefault();
    setSaving(true);
    const payload = {
      release_required: form.release_required,
      release_sent: form.release_sent,
      release_sent_date: form.release_sent_date || null,
      waiting_period_type: form.waiting_period_type || null,
      follow_up_date: effectiveFollowUp || null,
      blockout_date: form.blockout_date || null,
      carrier_id: form.carrier_id || null,
      notes: form.notes || null,
    };
    try {
      await contractRequestAPI.upsertReleaseFollowUp(request.request_id, payload);
      toast.success('Release follow-up saved');
      setEditOpen(false);
      onChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save release follow-up');
    } finally {
      setSaving(false);
    }
  };

  const handleComplete = async () => {
    setSaving(true);
    try {
      await contractRequestAPI.completeReleaseFollowUp(request.request_id, { completion_notes: completionNotes });
      toast.success('Release follow-up completed');
      setCompleteOpen(false);
      setCompletionNotes('');
      onChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to complete');
    } finally {
      setSaving(false);
    }
  };

  // Visibility rule: render only if there is an rfu OR the user is KR and can create one
  if (!rfu && !isKR) return null;

  const isCompleted = !!rfu?.completed_at;
  const today = new Date().toISOString().slice(0, 10);
  const isOverdue = !isCompleted && rfu?.follow_up_date && rfu.follow_up_date < today;
  const isDueToday = !isCompleted && rfu?.follow_up_date === today;

  return (
    <>
      <Card
        className={
          'border-l-4 ' +
          (isCompleted ? 'border-l-emerald-400' : isOverdue ? 'border-l-red-500' : isDueToday ? 'border-l-amber-500' : 'border-l-orange-400')
        }
        data-testid="release-followup-card"
      >
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-heading flex items-center gap-2">
              <CalendarClock size={20} className={isCompleted ? 'text-emerald-500' : isOverdue ? 'text-red-500' : 'text-orange-500'} />
              Release Follow-Up
              {isCompleted && <Badge className="bg-emerald-100 text-emerald-700">Completed</Badge>}
              {!isCompleted && isOverdue && <Badge className="bg-red-100 text-red-700">Overdue</Badge>}
              {!isCompleted && isDueToday && <Badge className="bg-amber-100 text-amber-700">Due Today</Badge>}
            </CardTitle>
            {isKR && (
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={openEdit} data-testid="rfu-edit-btn">
                  <Edit size={14} className="mr-1.5" />
                  {rfu ? 'Edit' : 'Set Pending Release'}
                </Button>
                {rfu && !isCompleted && (
                  <Button size="sm" onClick={() => setCompleteOpen(true)} data-testid="rfu-complete-btn">
                    <CheckCircle2 size={14} className="mr-1.5" /> Mark Completed
                  </Button>
                )}
              </div>
            )}
          </div>
        </CardHeader>
        {rfu ? (
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <Label className="text-slate-500 text-xs">Release Required</Label>
                <p className="font-medium text-slate-900">{rfu.release_required ? 'Yes' : 'No'}</p>
              </div>
              <div>
                <Label className="text-slate-500 text-xs">Release Sent</Label>
                <p className="font-medium text-slate-900">{rfu.release_sent ? 'Yes' : 'No'}</p>
              </div>
              <div>
                <Label className="text-slate-500 text-xs">Release Sent Date</Label>
                <p className="font-medium text-slate-900">{rfu.release_sent_date ? formatDate(rfu.release_sent_date) : '-'}</p>
              </div>
              <div>
                <Label className="text-slate-500 text-xs">Waiting Period</Label>
                <p className="font-medium text-slate-900">
                  {WAITING_PERIOD_OPTIONS.find(o => o.value === rfu.waiting_period_type)?.label || '-'}
                </p>
              </div>
              {rfu.blockout_date && (
                <div>
                  <Label className="text-slate-500 text-xs">Blockout Date</Label>
                  <p className="font-medium text-slate-900">{formatDate(rfu.blockout_date)}</p>
                </div>
              )}
              <div>
                <Label className="text-slate-500 text-xs">Follow-Up Date</Label>
                <p className={'font-semibold ' + (isOverdue ? 'text-red-600' : isDueToday ? 'text-amber-600' : 'text-slate-900')}>
                  {rfu.follow_up_date ? formatDate(rfu.follow_up_date) : '-'}
                </p>
              </div>
            </div>
            {rfu.notes && (
              <div>
                <Label className="text-slate-500 text-xs">Notes</Label>
                <p className="text-slate-700 text-sm whitespace-pre-wrap">{rfu.notes}</p>
              </div>
            )}
            {isCompleted && (
              <div className="border-t pt-3">
                <Label className="text-slate-500 text-xs">Completed</Label>
                <p className="text-sm text-slate-900">
                  {formatDate(rfu.completed_at)} by {rfu.completed_by}
                </p>
                {rfu.completion_notes && <p className="text-sm text-slate-700 mt-1">{rfu.completion_notes}</p>}
              </div>
            )}
          </CardContent>
        ) : (
          <CardContent>
            <div className="text-slate-500 text-sm flex items-start gap-2">
              <AlertTriangle size={16} className="text-orange-500 mt-0.5" />
              <span>This request has no release follow-up yet. Set it when the agent needs a release or waiting period before contracting.</span>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{rfu ? 'Edit Release Follow-Up' : 'Set Pending Release'}</DialogTitle>
            <DialogDescription>
              Track release/waiting period and schedule the follow-up reminder.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-3 rounded-lg border p-3">
                <Switch
                  id="release_required"
                  checked={form.release_required}
                  onCheckedChange={(v) => setForm(p => ({ ...p, release_required: v }))}
                  data-testid="rfu-release-required"
                />
                <Label htmlFor="release_required" className="cursor-pointer">Release Required</Label>
              </div>
              <div className="flex items-center gap-3 rounded-lg border p-3">
                <Switch
                  id="release_sent"
                  checked={form.release_sent}
                  onCheckedChange={(v) => setForm(p => ({ ...p, release_sent: v }))}
                  data-testid="rfu-release-sent"
                />
                <Label htmlFor="release_sent" className="cursor-pointer">Release Sent</Label>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Release Sent Date</Label>
                <Input
                  type="date"
                  value={form.release_sent_date}
                  onChange={(e) => { setForm(p => ({ ...p, release_sent_date: e.target.value })); setFollowUpManuallyEdited(false); }}
                  data-testid="rfu-sent-date"
                />
              </div>
              <div className="space-y-2">
                <Label>Waiting Period Type</Label>
                <Select
                  value={form.waiting_period_type || 'none'}
                  onValueChange={(v) => { setForm(p => ({ ...p, waiting_period_type: v === 'none' ? '' : v })); setFollowUpManuallyEdited(false); }}
                >
                  <SelectTrigger data-testid="rfu-waiting-type"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— None —</SelectItem>
                    {WAITING_PERIOD_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {form.waiting_period_type === 'blockout' && (
              <div className="space-y-2">
                <Label>Blockout Date *</Label>
                <Input
                  type="date"
                  required
                  value={form.blockout_date}
                  onChange={(e) => { setForm(p => ({ ...p, blockout_date: e.target.value })); setFollowUpManuallyEdited(false); }}
                  data-testid="rfu-blockout-date"
                />
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>
                  Follow-Up Date
                  <span className="ml-2 text-[10px] text-slate-400">
                    {!followUpManuallyEdited && autoFollowUp ? '(auto-calculated)' : '(manual override)'}
                  </span>
                </Label>
                <Input
                  type="date"
                  value={effectiveFollowUp}
                  onChange={(e) => { setForm(p => ({ ...p, follow_up_date: e.target.value })); setFollowUpManuallyEdited(true); }}
                  data-testid="rfu-follow-up-date"
                />
              </div>
              {carriers.length > 0 && (
                <div className="space-y-2">
                  <Label>Carrier</Label>
                  <Select
                    value={form.carrier_id || 'default'}
                    onValueChange={(v) => setForm(p => ({ ...p, carrier_id: v === 'default' ? (request?.carrier_id || '') : v }))}
                  >
                    <SelectTrigger data-testid="rfu-carrier"><SelectValue placeholder="Carrier" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="default">— Use Request's Carrier —</SelectItem>
                      {carriers.map(c => (
                        <SelectItem key={c.carrier_id} value={c.carrier_id}>{c.carrier_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                rows={3}
                value={form.notes}
                onChange={(e) => setForm(p => ({ ...p, notes: e.target.value }))}
                placeholder="Context, current FMO/upline, expected release process…"
                data-testid="rfu-notes"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={saving} data-testid="rfu-save-btn">
                <Send size={14} className="mr-1.5" />
                {saving ? 'Saving…' : (rfu ? 'Save Changes' : 'Set Pending Release')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Complete Dialog */}
      <Dialog open={completeOpen} onOpenChange={setCompleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mark Release Follow-Up Completed</DialogTitle>
            <DialogDescription>
              This stops future reminders. Status can be advanced separately via "Change Status".
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Completion Notes</Label>
            <Textarea
              rows={3}
              value={completionNotes}
              onChange={(e) => setCompletionNotes(e.target.value)}
              placeholder="What was the outcome?"
              data-testid="rfu-completion-notes"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCompleteOpen(false)}>Cancel</Button>
            <Button onClick={handleComplete} disabled={saving} data-testid="rfu-confirm-complete-btn">
              <CheckCircle2 size={14} className="mr-1.5" />
              {saving ? 'Completing…' : 'Mark Completed'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default ReleaseFollowUpCard;
